import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { ServiceProvidersApi, ServiceProvidersConstants } from '@frontend/api-service-providers';
import { useQuery } from '@frontend/react-query-helpers';
import { useGetCalendarStartEndDate } from './use-get-calendar-start-end-date';

type UseGetSchedulePulseInsuranceTabAppointmentsParamsType =
  | {
      // Case 1: selectedDate is required
      selectedDate: string;
      widgetStartDate?: never;
      widgetEndDate?: never;
      locationIds: string[];
    }
  | {
      // Case 2: widgetStartDate and widgetEndDate are required
      selectedDate?: never;
      widgetStartDate: string;
      widgetEndDate: string;
      locationIds: string[];
    };

type UseGetSchedulePulseInsuranceTabAppointmentsReturnType = {
  allAppointmentsData: Appointment[] | CalendarEvent[];
  isLoadingAppointmentsData: boolean;
  isScheduleV3FlagEnabled: boolean;
};

export const useGetSchedulePulseInsuranceTabAppointments = ({
  selectedDate,
  widgetStartDate,
  widgetEndDate,
  locationIds,
}: UseGetSchedulePulseInsuranceTabAppointmentsParamsType): UseGetSchedulePulseInsuranceTabAppointmentsReturnType => {
  let { startDate, endDate } = useGetCalendarStartEndDate(selectedDate || '');
  startDate = startDate || widgetStartDate || '';
  endDate = endDate || widgetEndDate || '';

  const { isScheduleV3FlagEnabled, isLoading: isLoadingFF } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  const { data: calendarEventsData, isLoading: isLoadingCalendarEvents } =
    SchedulerV3.Helpers.useGetCalendarEventsV3Data({
      selectedLocationIds: locationIds,
      includeAttendeeDetails: true,
      selectedDate: selectedDate || '',
      ...(widgetStartDate && { startDateTime: startDate }),
      ...(widgetStartDate && { endDateTime: endDate }),
      page: 1,
      limit: 500,
      enabled: !!locationIds.length && !!startDate && !!endDate && isScheduleV3FlagEnabled && !isLoadingFF,
    });

  const { data: allAppointments, isLoading: isLoadingAppointment } = useQuery({
    queryKey: ['listAppointments', { locationIds: locationIds, startDate, endDate }],
    queryFn: () =>
      ServiceProvidersApi.listAppointments({
        between: { start: startDate, end: endDate },
        equals: {
          locationIds: locationIds,
        },
      }),
    retry: 1,
    cacheTime: 10 * ServiceProvidersConstants.ONE_MINUTE_IN_MILLISECONDS, // 10 mins
    staleTime: 10 * ServiceProvidersConstants.ONE_MINUTE_IN_MILLISECONDS, // 10 mins
    enabled: !!locationIds?.length && !!startDate && !!endDate && !isScheduleV3FlagEnabled && !isLoadingFF,
    onSettled: () => SchedulerV3.trackScheduleAPIVersionChange('v2', { endpoint: 'listAppointments' }),
  });

  const isLoadingAppointmentsData = isScheduleV3FlagEnabled ? isLoadingCalendarEvents : isLoadingAppointment;

  const allAppointmentsData = isScheduleV3FlagEnabled
    ? calendarEventsData?.events || []
    : allAppointments?.appointments || [];

  return { allAppointmentsData, isLoadingAppointmentsData, isScheduleV3FlagEnabled };
};
